"use client";

import React from "react";
import { UserSelect } from "@/components/shared/UserSelect";
import { useUniversalAuth } from "@wtc/ui";
import { useToast } from "@/components/ui/toast";

interface AssignToUserSelectProps {
  currentUserId?: string;
  onAssign: (assignedTo: string | null, selectedKey: string) => Promise<void>;
  onSuccess?: () => void;
  disabled?: boolean;
  placeholder?: string;
  showUnassigned?: boolean;
  showAllUsers?: boolean;
  getSuccessMessage?: (assignedTo: string | null, selectedKey: string) => string;
  /** Called after successful assign with the new user id (or null if unassigned) */
  onSuccessWithValue?: (newUserId: string | null) => void;
  /** When true, expand inline (search + list below) instead of popover */
  expandInline?: boolean;
  /** When expandInline: "replace" (default) or "floating" */
  expandInlineVariant?: "replace" | "floating";
  className?: string;
}

export const AssignToUserSelect: React.FC<AssignToUserSelectProps> = ({
  currentUserId,
  onAssign,
  onSuccess,
  disabled = false,
  placeholder = "Assign to...",
  showUnassigned = true,
  showAllUsers = false,
  getSuccessMessage,
  onSuccessWithValue,
  expandInline = false,
  expandInlineVariant = "replace",
  className,
}) => {
  const { appUser } = useUniversalAuth();
  const { toast } = useToast();

  const handleUserAssignment = async (userId: string) => {
    if (userId === currentUserId) {
      return; // No change needed
    }

    try {
      let assignedTo: string | null = userId;
      if (userId === "me") {
        if (!appUser?.id) {
          throw new Error("You must be logged in to assign to yourself.");
        }
        assignedTo = appUser.id;
      } else if (userId === "unassigned") {
        assignedTo = null;
      }

      await onAssign(assignedTo, userId);

      const message = getSuccessMessage
        ? getSuccessMessage(assignedTo, userId)
        : assignedTo
          ? "Assignment updated"
          : "Assignment cleared";

      toast({
        title: "Assignment updated!",
        description: message,
        type: "success",
      });

      onSuccess?.();
      onSuccessWithValue?.(assignedTo);
    } catch (error: any) {
      console.error("Error assigning:", error);
      toast({
        title: "Failed to update assignment",
        description: error?.message || "An error occurred.",
        type: "error",
      });
    }
  };

  return (
    <UserSelect
      currentUserId={currentUserId}
      onUserSelect={handleUserAssignment}
      disabled={disabled}
      placeholder={placeholder}
      showUnassigned={showUnassigned}
      showAllUsers={showAllUsers}
      expandInline={expandInline}
      expandInlineVariant={expandInlineVariant}
      className={className}
    />
  );
};
